import React, {useCallback, useState} from 'react';
import {
  TableCell,
  TableRow,
  Select,
  MenuItem,
  Box,
  Button,
  SelectChangeEvent
} from '@mui/material';
import ActionButtons from './action-icons';
import {
  confirmEmployeeStatus,
  employeeButtonStyles,
  employeeMenuItemStyles,
  employeeSelectStyles,
  formatName,
  removeEmployeeFunc,
  updateEmployeeRole
} from './employee-utils';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import {getTranslatedRole, useTitleStyle} from '@/utils';
import {CompanyUser} from '@/types/user';
import {useTranslation} from 'react-i18next';
import {CompanyRole} from '@/constants';
import {RoleChangeDetails} from '@/types/common';
import {useCompanyContext} from '@/contexts/admin/companies';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {useSnackbar} from '@/contexts/snackbar';

interface EmployeeRowProps {
  employee: CompanyUser;
  companyId: string;
}

const EmployeeRow: React.FC<EmployeeRowProps> = (props) => {
  const {employee, companyId} = props;

  const {t} = useTranslation();

  const {showSnackbarMessage} = useSnackbar();

  const {refreshCompanyData} = useCompanyContext() || {};

  const nameStyle = useTitleStyle('10ch', undefined, 'sm', false);

  const [tempSelectedRole, setTempSelectedRole] = useState<CompanyRole | null>(
    null
  );
  const [selectedRole, setSelectedRole] = useState(employee.companies[0].role);
  const [removeConfirmationOpen, setRemoveConfirmationOpen] = useState(false);
  const [employeeToRemove, setEmployeeToRemove] = useState<string | null>(null);
  const [changeRoleConfirmationOpen, setChangeRoleConfirmationOpen] =
    useState(false);
  const [roleChangeDetails, setRoleChangeDetails] =
    useState<RoleChangeDetails>(null);

  const handleConfirmationUpdate = useCallback(
    (employeeId: string) => {
      confirmEmployeeStatus(
        employeeId,
        showSnackbarMessage,
        t,

        refreshCompanyData,
        companyId
      );
    },
    [showSnackbarMessage, t, companyId, refreshCompanyData]
  );

  const handleRemoveConfirmation = useCallback((employeeId: string) => {
    setEmployeeToRemove(employeeId);
    setRemoveConfirmationOpen(true);
  }, []);

  const handleRoleChangeConfirmation = useCallback(
    (details: RoleChangeDetails) => {
      if (details) {
        setRoleChangeDetails(details);
        setChangeRoleConfirmationOpen(true);
      }
    },
    []
  );

  const handleRoleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newRole = event.target.value as CompanyRole;
      setTempSelectedRole(newRole);
      handleRoleChangeConfirmation({
        employeeId: employee._id,
        updatedRole: newRole
      });
    },
    [employee, handleRoleChangeConfirmation]
  );

  const handleRoleUpdate = useCallback(() => {
    if (roleChangeDetails && tempSelectedRole) {
      setSelectedRole(tempSelectedRole);
      updateEmployeeRole(
        roleChangeDetails.employeeId,
        roleChangeDetails.updatedRole,
        showSnackbarMessage,
        t,
        refreshCompanyData,
        companyId
      );
      setRoleChangeDetails(null);
      setChangeRoleConfirmationOpen(false);
    }
  }, [
    roleChangeDetails,
    tempSelectedRole,
    showSnackbarMessage,
    t,
    refreshCompanyData,
    companyId
  ]);

  const handleCloseRemoveConfirmation = useCallback(() => {
    setRemoveConfirmationOpen(false);
  }, []);

  const handleCloseChangesConfirmation = useCallback(() => {
    setChangeRoleConfirmationOpen(false);
  }, []);

  const handleRemoveClick = useCallback(async () => {
    if (employeeToRemove) {
      await removeEmployeeFunc(
        employeeToRemove,
        showSnackbarMessage,
        t,
        refreshCompanyData,
        companyId
      );
      setEmployeeToRemove(null);
      setRemoveConfirmationOpen(false);
    }
  }, [employeeToRemove, showSnackbarMessage, t, refreshCompanyData, companyId]);

  return (
    <>
      <TableRow>
        <TableCell sx={{height: '80px', width: '5%'}}>
          <ActionButtons
            employeeId={employee._id}
            handleRemoveConfirmation={handleRemoveConfirmation}
          />
        </TableCell>

        <TableCell sx={{height: '80px', width: '15%'}}>
          <ResponsiveTypography sx={nameStyle} verySmallXs>
            {formatName(employee.name)}
          </ResponsiveTypography>
        </TableCell>

        <TableCell sx={{height: '80px', width: '15%'}}>
          <ResponsiveTypography sx={nameStyle} verySmallXs>
            {formatName(employee.email)}
          </ResponsiveTypography>
        </TableCell>

        <TableCell sx={{height: '80px', width: '10%'}}>
          <Select
            value={selectedRole}
            onChange={handleRoleChange}
            sx={employeeSelectStyles}
            disabled={selectedRole === 'TRAINER'}
          >
            {Object.values(CompanyRole).map((role) => {
              if (
                role === CompanyRole.TRAINER &&
                selectedRole !== CompanyRole.TRAINER
              ) {
                return null;
              }

              return (
                <MenuItem key={role} value={role} sx={employeeMenuItemStyles}>
                  {getTranslatedRole(t, role)}
                </MenuItem>
              );
            })}
          </Select>
        </TableCell>

        <TableCell
          sx={{
            height: '80px',
            width: '15%',
            // paddingRight: { xs: 0, sm: 8 },
            marginRight: '-10px'
          }}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            {employee.companies[0].confirmed ? (
              <ResponsiveTypography verySmallXs>
                {t('Yes')}
              </ResponsiveTypography>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleConfirmationUpdate(employee._id)}
                sx={employeeButtonStyles}
              >
                {t('confirm')}
              </Button>
            )}
          </Box>
        </TableCell>
      </TableRow>
      <ConfirmationDialog
        open={removeConfirmationOpen}
        title={t('remove_employee_dialog_title')}
        message={t('remove_employee_dialog_content')}
        onConfirm={handleRemoveClick}
        onCancel={handleCloseRemoveConfirmation}
        warn
      />

      <ConfirmationDialog
        open={changeRoleConfirmationOpen}
        title={t('role_change_confirmation_title')}
        message={
          roleChangeDetails?.updatedRole
            ? t('role_change_confirmation_text', {
                role: getTranslatedRole(t, roleChangeDetails.updatedRole)
              })
            : ''
        }
        onConfirm={handleRoleUpdate}
        onCancel={handleCloseChangesConfirmation}
      />
    </>
  );
};

export default EmployeeRow;
